/* File: PublicFormPage.jsx
 * Path: ananunadm/src/pages/Forms/PublicFormPage.jsx
 *
 * Public, no-login respondent page mounted at /f/:slug. Handles password gate,
 * optional name/email collection, honeypot anti-spam, scheduled/closed states.
 * This component is rendered OUTSIDE the authenticated shell (see App.js).
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
	MantineProvider, Center, Loader, Card, Title, Text, Alert, Stack,
	PasswordInput, Button, TextInput, Container
} from "@mantine/core";
import { IconLock, IconCircleCheck, IconClock } from "@tabler/icons-react";
import formsApi from "../../api/formsApi";
import { useLang } from "../../helpers/language";
import FormRunner from "../../components/Forms/FormRunner";
import { pickLocale, tr } from "./formsShared";

export default function PublicFormPage() {
	const { slug } = useParams();
	const langCtx = useLang() || {};
	const lang = langCtx.lang || "en";
	const [state, setState] = useState(null);
	const [password, setPassword] = useState("");
	const [unlocked, setUnlocked] = useState(false);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [hp, setHp] = useState(""); // honeypot
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(null);
	const [pwError, setPwError] = useState("");

	useEffect(() => { formsApi.publicForm(slug).then(setState).catch(() => setState({ error: "Not found" })); }, [slug]);

	const verify = () => {
		formsApi.publicVerifyPassword(slug, password)
			.then((r) => { if (r.ok) { setUnlocked(true); setPwError(""); } else setPwError(tr("password", lang)); })
			.catch(() => setPwError(tr("password", lang)));
	};

	const submit = (payload) => {
		setSubmitting(true);
		formsApi.publicSubmit(slug, { ...payload, password, respondentName: name, respondentEmail: email, _hp: hp })
			.then((r) => setDone(r))
			.catch((e) => setPwError(e?.response?.data?.error || "Submit failed"))
			.finally(() => setSubmitting(false));
	};

	const content = () => {
		if (!state) return <Center mih={300}><Loader /></Center>;
		if (done) {
			return (
				<Card withBorder radius="md" p="xl">
					<Stack align="center">
						<IconCircleCheck size={56} color="var(--mantine-color-green-6)" />
						<Title order={3}>{tr("thankYou", lang)}</Title>
						<Text color="dimmed">{pickLocale(done.thankYouMessage, lang) || tr("submitted", lang)}</Text>
					</Stack>
				</Card>
			);
		}
		if (state.error) return <Alert color="red">{state.error}</Alert>;
		if (state.locked) {
			if (state.reason === "scheduled") {
				return <Alert icon={<IconClock size={18} />} color="blue" title={pickLocale(state.form?.title, lang)}>
					{tr("startsAt", lang)} {state.startAt ? new Date(state.startAt).toLocaleString() : ""}
				</Alert>;
			}
			return <Alert icon={<IconLock size={18} />} color="gray" title={tr("closed", lang)}>
				{pickLocale(state.closeMessage, lang) || pickLocale(state.form?.title, lang)}
			</Alert>;
		}
		if (state.passwordRequired && !unlocked) {
			return (
				<Card withBorder radius="md" p="lg">
					<Stack>
						<Title order={4}>{tr("passwordRequired", lang)}</Title>
						<PasswordInput label={tr("password", lang)} value={password} onChange={(e) => setPassword(e.currentTarget.value)} error={pwError} />
						<Button onClick={verify}>{tr("next", lang)}</Button>
					</Stack>
				</Card>
			);
		}

		const form = state.form;
		return (
			<Stack>
				{(form.collectName || form.collectEmail) && (
					<Card withBorder radius="md">
						<Stack>
							{form.collectName && <TextInput label={tr("nameLabel", lang)} value={name} onChange={(e) => setName(e.currentTarget.value)} required />}
							{form.collectEmail && <TextInput type="email" label={tr("emailLabel", lang)} value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />}
						</Stack>
					</Card>
				)}
				{/* honeypot: hidden from humans, bots fill it → server drops the response */}
				<input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off"
					style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />
				{pwError && <Alert color="red">{pwError}</Alert>}
				<FormRunner form={form} lang={lang} onSubmit={submit} submitting={submitting} />
			</Stack>
		);
	};

	return (
		<MantineProvider theme={{ colorScheme: "light" }} withGlobalStyles withNormalizeCSS>
			<div style={{ minHeight: "100vh", background: "#f4f6fb", paddingTop: 32, paddingBottom: 64 }}>
				<Container size="sm">{content()}</Container>
			</div>
		</MantineProvider>
	);
}
